// Shared presentational pieces for the Reports hub: severity/status chips,
// the incident card (used by My Reports and Nearby) and its mini lifecycle
// stepper / expanded status timeline.
import { type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, useState } from 'react';

import {
	type IIncident,
	INCIDENT_CATEGORIES,
	INCIDENT_SEVERITIES,
	INCIDENT_STATUS_LABELS,
	INCIDENT_STATUS_ORDER,
	INCIDENT_STATUS_TONES,
	type IncidentSeverity,
	type IncidentStatus,
} from '../../../interface/vesr';
import Lightbox, { type ILightboxItem } from '../child-agent/Lightbox';
import { relativeTime } from '../child-agent/utils';
import { CategoryGlyph } from './icons';
import { isVideoAttachment } from './utils';

export const severityOf = (severity: IncidentSeverity) =>
	INCIDENT_SEVERITIES.find((s) => s.value === severity) ?? INCIDENT_SEVERITIES[2];

export const categoryOf = (category: IIncident['category']) =>
	INCIDENT_CATEGORIES.find((c) => c.value === category) ?? INCIDENT_CATEGORIES[5];

export const SeverityChip = ({ severity }: { severity: IncidentSeverity }) => {
	const opt = severityOf(severity);
	return (
		<span
			className='rh-sev-chip'
			style={{ '--sev': opt.color, '--sev-bg': opt.bg } as CSSProperties}
		>
			<span
				className='rh-sev-chip__dot'
				aria-hidden='true'
			/>
			{opt.label}
		</span>
	);
};

export const StatusBadge = ({ status }: { status: IncidentStatus }) => (
	<span className={`rh-status-badge rh-status-badge--${INCIDENT_STATUS_TONES[status] ?? 'neutral'}`}>
		{INCIDENT_STATUS_LABELS[status] ?? status}
	</span>
);

/**
 * Compact 5-node lifecycle track (Reported→…→Closed). Completed nodes filled,
 * the current node pulses, future nodes hollow. Rendered as an ordered list
 * with aria-current for screen readers.
 */
export const StatusMiniStepper = ({ status }: { status: IncidentStatus }) => {
	const activeIndex = Math.max(0, INCIDENT_STATUS_ORDER.indexOf(status));
	return (
		<ol
			className='rh-ministep'
			aria-label='Report status progression'
		>
			{INCIDENT_STATUS_ORDER.map((step, i) => {
				const stateClass = i < activeIndex ? 'is-done' : i === activeIndex ? 'is-current' : '';
				return (
					<li
						key={step}
						className={`rh-ministep__node ${stateClass}`}
						aria-current={i === activeIndex ? 'step' : undefined}
					>
						<span
							className='rh-ministep__dot'
							aria-hidden='true'
						/>
						<span className='rh-ministep__label'>{INCIDENT_STATUS_LABELS[step]}</span>
					</li>
				);
			})}
		</ol>
	);
};

/**
 * One line describing the latest thing the situation room did to a report, or
 * null while it is still only "Reported" (nothing has happened yet, and saying
 * so would be noise).
 */
export const lastActionLine = (incident: IIncident): string | null => {
	const history = incident.statusHistory ?? [];
	if (!history.length) return null;

	const latest = [...history]
		.filter((h) => h.toStatus && h.toStatus !== 'Reported')
		.sort((a, b) => new Date(b.changedAt || 0).getTime() - new Date(a.changedAt || 0).getTime())[0];
	if (!latest) return null;

	const verb =
		latest.toStatus === 'Acknowledged'
			? 'Seen by the situation room'
			: latest.toStatus === 'InProgress'
				? 'Being worked on'
				: latest.toStatus === 'Resolved'
					? 'Marked resolved'
					: latest.toStatus === 'Closed'
						? 'Closed'
						: `Moved to ${latest.toStatus}`;

	return [verb, latest.changedByName, relativeTime(latest.changedAt)].filter(Boolean).join(' · ');
};

/** Vertical status history timeline for the expanded card. */
const StatusTimeline = ({ incident }: { incident: IIncident }) => {
	if (!incident.statusHistory?.length) return null;
	return (
		<div className='rh-timeline'>
			<p className='rh-timeline__head'>Status history</p>
			<ol className='rh-timeline__list'>
				{incident.statusHistory.map((entry, i) => (
					<li
						className='rh-timeline__item'
						key={`${entry.toStatus}-${entry.changedAt}-${i}`}
					>
						<span
							className='rh-timeline__node'
							aria-hidden='true'
						/>
						<div className='rh-timeline__body'>
							<span className='rh-timeline__transition'>
								{INCIDENT_STATUS_LABELS[entry.toStatus as IncidentStatus] ?? entry.toStatus}
							</span>
							<span className='rh-timeline__meta'>
								{[entry.changedByName, relativeTime(entry.changedAt)].filter(Boolean).join(' · ')}
							</span>
							{entry.reason ? <span className='rh-timeline__reason'>{entry.reason}</span> : null}
						</div>
					</li>
				))}
			</ol>
		</div>
	);
};

interface IIncidentCardProps {
	incident: IIncident;
	/** Distance pill (Nearby feed) — omitted on My Reports. */
	distance?: string;
	/** Ownership chrome: referenceId + copy + expandable detail. */
	mine?: boolean;
	expanded?: boolean;
	onToggle?: () => void;
	/** One-shot highlight wash after a live status change. */
	highlight?: boolean;
	onCopyRef?: (referenceId: string) => void;
	copied?: boolean;
}

export const IncidentCard = ({
	incident,
	distance,
	mine,
	expanded,
	onToggle,
	highlight,
	onCopyRef,
	copied,
}: IIncidentCardProps) => {
	const [lightbox, setLightbox] = useState<{ items: ILightboxItem[]; index: number } | null>(null);
	const sev = severityOf(incident.severity);
	const cat = categoryOf(incident.category);
	// One tile per attachment: thumb (videos get a Cloudinary jpg poster) +
	// the full media for the lightbox, which plays video inline.
	const media = (incident.attachments ?? [])
		.map((a) => ({
			thumb: a.thumbnailUrl || a.fileUrl,
			full: a.fileUrl || a.thumbnailUrl,
			isVideo: isVideoAttachment(a),
		}))
		.filter((m) => !!m.thumb);
	const lightboxItems: ILightboxItem[] = media.map((m) => ({
		url: m.full || m.thumb || '',
		type: m.isVideo ? 'video' : 'image',
	}));
	const isResolved = incident.status === 'Resolved' || incident.status === 'Closed';
	// The most recent thing the situation room actually DID. Surfaced on the
	// collapsed card because an agent should not have to open a report to learn
	// somebody picked it up — that silence is the whole reason reporting feels
	// like shouting into a void.
	const latestAction = mine ? lastActionLine(incident) : null;

	return (
		<article
			className={`rh-card ${highlight ? 'rh-card--live' : ''} ${expanded ? 'rh-card--expanded' : ''}`}
			style={{ '--sev': sev.color, '--accent': cat.accent } as CSSProperties}
		>
			<div
				className={`rh-card__main ${onToggle ? 'rh-card__main--tappable' : ''}`}
				// Interactive props are spread so the element only carries a button
				// role + tabIndex when the card is actually expandable (My Reports).
				{...(onToggle
					? {
							'role': 'button' as const,
							'tabIndex': 0,
							'aria-expanded': !!expanded,
							'onClick': onToggle,
							'onKeyDown': (e: ReactKeyboardEvent<HTMLDivElement>) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									onToggle();
								}
							},
						}
					: {})}
			>
				<div className='rh-card__head'>
					<span
						className='rh-card__cat'
						aria-hidden='true'
					>
						<CategoryGlyph category={incident.category} />
					</span>
					<span className='rh-card__cat-label'>{cat.label}</span>
					<SeverityChip severity={incident.severity} />
					<span className='rh-card__spacer' />
					{distance ? <span className='rh-card__distance'>{distance}</span> : null}
					{incident.createdAt ? <span className='rh-card__time'>{relativeTime(incident.createdAt)}</span> : null}
				</div>

				{mine && incident.referenceId ? (
					<div className='rh-card__ref-row'>
						<code className='rh-card__ref'>#{incident.referenceId}</code>
						{onCopyRef ? (
							<button
								type='button'
								className='rh-card__copy'
								aria-label={`Copy report number ${incident.referenceId}`}
								onClick={(e) => {
									e.stopPropagation();
									onCopyRef(incident.referenceId);
								}}
							>
								{copied ? '✓ Copied' : 'Copy'}
							</button>
						) : null}
						<span className='rh-card__spacer' />
						<StatusBadge status={incident.status} />
					</div>
				) : null}

				<p className='rh-card__title'>{incident.title}</p>
				{incident.description ? <p className='rh-card__body'>{incident.description}</p> : null}

				{media.length ? (
					<div className='rh-card__media'>
						{media.slice(0, 3).map((m, i) => (
							<button
								type='button'
								className={`rh-card__thumb ${m.isVideo ? 'rh-card__thumb--video' : ''}`}
								key={m.thumb}
								aria-label={`Open report ${m.isVideo ? 'video' : 'photo'} ${i + 1}`}
								onClick={(e) => {
									e.stopPropagation();
									setLightbox({ items: lightboxItems, index: i });
								}}
							>
								<img
									src={m.thumb}
									alt={`Report evidence ${i + 1}`}
									loading='lazy'
								/>
								{m.isVideo ? (
									<span
										className='rh-card__play'
										aria-hidden='true'
									>
										<svg
											width='14'
											height='14'
											viewBox='0 0 24 24'
											fill='currentColor'
											aria-hidden='true'
										>
											<path d='M8 5.5v13l11-6.5-11-6.5Z' />
										</svg>
									</span>
								) : null}
							</button>
						))}
						{media.length > 3 ? <span className='rh-card__more'>+{media.length - 3}</span> : null}
					</div>
				) : null}

				{mine ? <StatusMiniStepper status={incident.status} /> : <StatusBadge status={incident.status} />}

				{latestAction ? <p className='rh-card__last-action'>{latestAction}</p> : null}
			</div>

			{expanded ? (
				<div className='rh-card__detail'>
					<StatusTimeline incident={incident} />
					{isResolved && incident.resolutionNotes ? (
						<div className='rh-card__resolution'>
							<span className='rh-card__resolution-head'>✓ Resolution</span>
							<p className='rh-card__resolution-body'>{incident.resolutionNotes}</p>
						</div>
					) : null}
				</div>
			) : null}

			{lightbox ? (
				<Lightbox
					images={lightbox.items}
					startIndex={lightbox.index}
					label='Report'
					onClose={() => setLightbox(null)}
				/>
			) : null}
		</article>
	);
};
